import React, { memo, useCallback, useMemo, useState } from 'react';
import { Row, useExpanded, useGroupBy, useSortBy, useTable } from 'react-table';
import styled, { css } from 'styled-components';
import { ITableProps } from './models';
import arrowDown from '../../assets/arrow-down.svg';
import sortArrowDown from '../../assets/arrow-down-grey.svg';
import arrowUp from '../../assets/arrow-up-blue.svg';
import sortArrowUp from '../../assets/arrow-up-grey.svg';
import { Icon } from '../../components/icon/Icon';
import { ActionsMenu } from './actions-menu/actions-menu.component';
import { VirtualItem, useVirtualizer } from '@tanstack/react-virtual';

const colors = {
  hoverLightGray: '#F2F4F7',
  borderGrey: '#D9DEE8',
  darkBlue: '#002169',
  lightGreen: '#F5FCF3',
  grey: '#FAFBFC',
  darkGrey: '#8090b4',
};

const SBaseTableRow = styled.tr(
  () => css`
    box-sizing: border-box;
    justify-content: space-between;
    padding: 0px;
    height: 48px;
    width: 100%;
    min-width: max-content;
    align-items: center;
  `
);

const STableRow = styled(SBaseTableRow)<{ isSmallRow?: boolean }>(
  ({ isSmallRow }) => `
    height: ${isSmallRow ? '24px' : '48px'};
    align-items: end;
    &:hover {
        background-color: ${colors.hoverLightGray};
    };
  `
);

const STableHeaderSmallCell = styled.th(
  () => css`
    ${SBaseCell}
    background-color: #fff;
    max-width: 20px;
    min-width: 20px;
    width: 20px;
  `
);

const smallCellStyle = () => css`
  ${SBaseCell}
  background-color: #fff;
  max-width: 20px;
  min-width: 20px;
  width: 20px;
`;

const STableSmallCell = styled.td(smallCellStyle);

const STableSmallHeaderCell = styled.th(smallCellStyle);

const STableExpandableRow = styled(SBaseTableRow)(
  () => css`
    height: auto;
    align-items: end;
    background-color: #fff !important;
  `
);

const STableExpandableCell = styled.td(
  () => css`
    width: 100%;
    background-color: #fff !important;
    height: 100%;
  `
);

const STableSummaryRow = styled(SBaseTableRow)`
  &:hover > td {
    background-color: ${colors.hoverLightGray};
  }
  &:last-child {
    border-bottom: 1px solid ${colors.borderGrey};
  }
`;
const STableHeaderRow = styled(SBaseTableRow)`
  background-color: #fff;
  align-items: end;
`;

const SBaseCell = css`
  font-family: 'Poppins';
  font-style: normal;
  font-size: 12px;
  text-align: center;
  padding: 0 10px;
  border-bottom: 1px solid ${colors.borderGrey};
  min-width: 150px;
  color: ${colors.darkBlue};
  -webkit-line-clamp: 2;
  text-overflow: ellipsis;
  text-align: center;
  justify-content: center;
  align-items: center;
`;

const STableCell = styled.td<{
  cellAmount: number;
  cellIndex?: number;
  isGrouped?: boolean;
  groupBy?: string;
  isGroupedCell?: boolean;
  isExpanded?: boolean;
  isText?: boolean;
  colSpan?: number;
}>(
  ({ cellAmount, cellIndex, isGrouped, groupBy, isGroupedCell, isExpanded, isText }) => css`
    ${SBaseCell}
    font-weight: 400;
    line-height: 18px;
    align-items: center;
    width: calc(100% / ${cellAmount});
    text-align: ${isText ? 'start' : 'center'};
    background-color: ${isGrouped && isExpanded ? colors.hoverLightGray : cellIndex % 2 === 0 ? '#fff' : colors.grey};
    max-width: calc(100% / ${cellAmount}) !important;
    min-width: 150px;
    &:first-child {
      justify-content: start;
      background-color: ${!(isGrouped && isExpanded) && groupBy && !isGroupedCell && '#fff'};
      border: ${!(isGrouped && isExpanded) && groupBy && !isGroupedCell && '0'};
      border-top: ${isExpanded && isGrouped && `1px solid ${colors.borderGrey}`};
      border-top: ${isGrouped && isGroupedCell && `1px solid ${colors.borderGrey}`};
    }
  `
);

const SSummaryCell = styled(STableCell)<{ isSummaryFirstCol?: boolean; isText?: boolean }>(
  ({ isSummaryFirstCol, isText }) => `
background-color:${colors.lightGreen};
text-align: ${isText ? 'start' : 'center'};

& .summary-title{
  font-weight: 600;
  color: ${colors.darkBlue};
}
&:first-child {
  border-bottom: ${isSummaryFirstCol ? 'none' : `1px solid ${colors.borderGrey}`};
}
`
);

const STableHeader = styled.th<{ cellAmount: number; cellIndex?: number }>(
  ({ cellAmount, cellIndex }) => css`
    ${SBaseCell}
    font-weight: 600;
    line-height: 21px;
    width: calc(100% / ${cellAmount});
    max-width: calc(100% / ${cellAmount});
    min-width: 150px;
    text-align: ${cellIndex ? 'center' : 'start'};
    background-color: ${cellIndex % 2 === 0 ? '#fff' : colors.grey};
    height: 100%;

    &:first-child {
      justify-content: start;
    }
  `
);

const SSpan = styled.span(
  () => css`
    display: flex;
  `
);

const STable = styled.table(
  () => css`
    max-height: 100%;
    width: 100%;
    max-width: 100%;
    border-collapse: collapse;
  `
);

const SStickyHeader = styled.thead(
  () => css`
    position: sticky;
    top: 0;
    background-color: #fff;
    width: 100%;
    min-width: 150px;
  `
);

const SSortSpan = styled.span(
  () => css`
    width: 12px;
    margin-left: 5px;
    position: absolute;
    text-align: center;
  `
);

const SIconSpan = styled.span(
  () => css`
    align-self: center;
  `
);

const SContainerDiv = styled.div(
  () => css`
    height: 400px;
    overflow: auto;
  `
);

const SCellSpan = styled.span(
  () => css`
    display: -webkit-box;
    min-width: 150px;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  `
);

const STableBody = styled.tbody(
  () => css`
    width: 100%;

    & tr:hover td {
      background-color: ${colors.hoverLightGray};
    }
  `
);

const SIcon = styled(Icon)`
  width: 12px;
  height: 7.4px;
  border-radius: 50px;
  margin-right: 18px;
  align-self: center;
  display: flex;
  color: ${colors.darkBlue};
`;
const SSortIcon = styled(Icon)`
  width: 9.17px;
  height: 6px;
  border-radius: 50px;
  flex: none;
  margin-right: 28px;
  order: 0;
  align-self: center;
  flex-grow: 0;
  color: ${colors.darkGrey};
`;

export const TableV2 = memo(
  ({
    columns,
    headerClass,
    data,
    groupBy,
    summaryRows,
    hideSummaryRow,
    isFixedGroupBy,
    dataAt,
    renderExpandableTemplate,
    actionsMenu,
    emptyStateTemplate = <></>,
    hasVirtualScroll = false,
    manualSortCB,
    dataTestId,
    expandRowId,
    onExpandRow,
    onCollapseRow,
    onRowClick,
  }: ITableProps) => {
    const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      rows,
      prepareRow,
      state: { expanded, sortBy },
      toggleGroupBy,
      setGroupBy,
      toggleAllRowsExpanded,
      toggleRowExpanded,
    } = useTable(
      {
        columns,
        data,
        groupBy: groupBy,
        manualSortBy: !!manualSortCB,
      },
      useGroupBy,
      useSortBy,
      useExpanded
    );
    const emptyState = data.length === 0;
    const parentRef = React.useRef();

    // Handling expandable area with virtual scroll
    const expandableAreaRef = React.useRef();

    // Virtual scroll area
    const virtualizer = useVirtualizer({
      count: rows.length,
      getScrollElement: () => parentRef.current,
      estimateSize: () => 48,
    });

    const printSummaryRow = () => {
      return !hideSummaryRow && summaryRows
        ? summaryRows.map((summaryRow, rowIndex) => (
            <STableSummaryRow>
              {columns.map((column, index) => {
                const row = column.accessor;
                const isFirstCell = index === 0;
                const isFirstRow = rowIndex === 0;
                const isTitle = isFirstCell && isFirstRow;
                const isTextContent = typeof summaryRow[row] === 'string';
                return (
                  <SSummaryCell
                    cellAmount={Object.keys(summaryRow).length}
                    isSummaryFirstCol={summaryRows?.length > 1}
                    isText={isTitle || isTextContent}
                  >
                    {isTitle ? (
                      <span className="summary-title">Summary</span>
                    ) : (
                      column.Cell({ value: summaryRow[row] }) || summaryRow[row]
                    )}
                  </SSummaryCell>
                );
              })}
            </STableSummaryRow>
          ))
        : '';
    };

    const toggleRowExpand = useCallback(
      (currentRow: Row) => {
        if (expanded[currentRow.id]) {
          toggleRowExpanded(currentRow.id, false);
          onCollapseRow?.(currentRow);
        } else {
          rows.forEach((row) => {
            if (row.id !== currentRow.id && row.isExpanded) {
              toggleRowExpanded(row.id, false);
              onCollapseRow?.(row);
            }
          });
          toggleRowExpanded(currentRow.id, true);
          onExpandRow?.(currentRow);
        }
      },
      [expanded]
    );

    const isRowExpanded = useMemo(() => {
      return expanded && Object.keys(expanded).length > 0;
    }, [expanded]);

    React.useEffect(() => {
      if (manualSortCB) {
        manualSortCB(sortBy);
      }
    }, [sortBy, manualSortCB]);

    React.useEffect(() => {
      if (isFixedGroupBy) {
        toggleAllRowsExpanded(true);
      }
      toggleGroupBy(groupBy, true);
    }, [data]);

    React.useEffect(() => {
      toggleGroupBy(groupBy, true);
      setGroupBy([groupBy]);
    }, [groupBy]);

    React.useEffect(() => {
      if (expandRowId) {
        rows?.forEach((row) => {
          if (row.id === expandRowId) {
            row?.toggleRowExpanded(true);
            onExpandRow?.(row);
          } else {
            if (row.isExpanded) {
              onCollapseRow?.(row);
            }
            row?.toggleRowExpanded(false);
          }
        });
      }
    }, [rows, expandRowId]);

    const printFixedGroupBy = (cell: any, row) => {
      if (!row.isExpanded) {
        toggleAllRowsExpanded(true);
      }
      return <>{cell.render('Cell')}</>;
    };

    const renderRow = (virtualRow: VirtualItem, index: number) => {
      const row = rows[virtualRow.index];
      prepareRow(row);
      const { key: rowKey, ...rowProps } = row.getRowProps();
      return (
        <React.Fragment>
          <STableRow
            isSmallRow={row.isGrouped && isFixedGroupBy}
            {...rowProps}
            key={row.id}
            onClick={() => {
              onRowClick?.(row);
            }}
            style={{
              height: `${virtualRow.size}px`,
              transform: `translateY(${virtualRow.start - index * virtualRow.size}px)`,
            }}
          >
            {renderExpandableTemplate && (
              <STableSmallCell>
                <SSpan>
                  <SIconSpan {...row.getToggleRowExpandedProps()} onClick={() => toggleRowExpand(row)}>
                    {row.isExpanded ? <SIcon src={arrowUp} /> : <SIcon src={arrowDown} />}
                  </SIconSpan>
                </SSpan>
              </STableSmallCell>
            )}
            {row.cells.map((cell, index) => {
              const { key: cellKey, ...cellProps } = cell.getCellProps();
              return (
                <STableCell
                  key={cellKey}
                  cellAmount={row.cells.length}
                  cellIndex={index}
                  {...cellProps}
                  isGrouped={row.isGrouped}
                  isText={typeof cell.value === 'string'}
                  isExpanded={row.isExpanded}
                  groupBy={groupBy}
                  isGroupedCell={cell.isGrouped}
                >
                  {cell.isGrouped ? (
                    isFixedGroupBy ? (
                      printFixedGroupBy(cell, row)
                    ) : (
                      // If it's a grouped cell, add an expander and row count
                      <SSpan>
                        <SIconSpan {...row.getToggleRowExpandedProps()}>
                          {row.isExpanded ? <SIcon src={arrowUp} /> : <SIcon src={arrowDown} />}
                        </SIconSpan>
                        <SCellSpan>{cell.render('Cell')}</SCellSpan>
                      </SSpan>
                    )
                  ) : row.isGrouped && isFixedGroupBy ? (
                    <SCellSpan>{}</SCellSpan> // If isFixedGroupBy and grouped cell - render empty cell
                  ) : cell.isAggregated ? (
                    // If the cell is aggregated, use the Aggregated
                    // renderer for cell
                    <SCellSpan>{cell.render('Aggregated')}</SCellSpan>
                  ) : cell.isPlaceholder ? null : ( // For cells with repeated values, render null
                    // Otherwise, just render the regular cell
                    <SCellSpan>{cell.render('Cell')}</SCellSpan>
                  )}
                </STableCell>
              );
            })}
            {actionsMenu && (
              <STableSmallCell key={`action-menu-cell-${rowKey}`}>
                <ActionsMenu {...actionsMenu} rowData={row.original} />
              </STableSmallCell>
            )}
          </STableRow>
          {renderExpandableTemplate && row.isExpanded && (
            <STableExpandableRow ref={expandableAreaRef} key={`expandable-row-${rowKey}`}>
              <STableExpandableCell key={`expandable-column-${rowKey}`} colSpan={columns.length + 1}>
                {renderExpandableTemplate(row.original)}
              </STableExpandableCell>
            </STableExpandableRow>
          )}
        </React.Fragment>
      );
    };

    return (
      <SContainerDiv ref={parentRef}>
        <div
          data-at={dataAt}
          style={{
            height: `${virtualizer.getTotalSize()}px`,
          }}
        >
          <STable {...getTableProps()} data-testid={dataTestId}>
            <SStickyHeader className={headerClass}>
              {headerGroups.map((headerGroup) => (
                <STableHeaderRow {...headerGroup.getHeaderGroupProps()}>
                  {renderExpandableTemplate && <STableHeaderSmallCell></STableHeaderSmallCell>}
                  {headerGroup.headers.map((column, index) => (
                    <STableHeader
                      cellIndex={index}
                      cellAmount={headerGroup.headers.length}
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                    >
                      {column.canGroupBy && column.id === groupBy ? (
                        <span {...column.getGroupByToggleProps()}>{}</span>
                      ) : null}
                      {column.render('Header')}
                      <SSortSpan>
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <SSortIcon src={sortArrowDown} />
                          ) : (
                            <SSortIcon src={sortArrowUp} />
                          )
                        ) : (
                          ''
                        )}
                      </SSortSpan>
                    </STableHeader>
                  ))}
                  {actionsMenu && <STableSmallHeaderCell></STableSmallHeaderCell>}
                </STableHeaderRow>
              ))}
            </SStickyHeader>
            <STableBody {...getTableBodyProps()}>
              {printSummaryRow()}
              {!emptyState ? (
                virtualizer.getVirtualItems().map((row, index) => {
                  return renderRow(row, index);
                })
              ) : (
                <STableExpandableRow>
                  <STableExpandableCell colSpan={columns.length + 1}>{emptyStateTemplate}</STableExpandableCell>
                </STableExpandableRow>
              )}
            </STableBody>
          </STable>
        </div>
      </SContainerDiv>
    );
  }
);
